// =============================================================================
// VulnTracker — About Page
// =============================================================================
import {
  ShieldExclamationIcon,
  LockClosedIcon,
  BoltIcon,
  BellAlertIcon,
  CubeTransparentIcon,
  CircleStackIcon,
  CodeBracketIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    icon: CubeTransparentIcon,
    text: 'Ingest SBOMs from CycloneDX JSON/XML, SPDX, lock-files, and manual registrations',
  },
  {
    icon: CircleStackIcon,
    text: 'Continuous NVD & OSV ingestion — component inventory matched 24 × 7 automatically',
  },
  {
    icon: BoltIcon,
    text: 'Real-time vulnerability matching — new CVEs surface affected projects within minutes',
  },
  {
    icon: BellAlertIcon,
    text: 'Configurable notification rules with multi-channel delivery history',
  },
  {
    icon: CodeBracketIcon,
    text: 'REST API for CI/CD pipeline integration and programmatic access',
  },
  {
    icon: SwatchIcon,
    text: 'Dark sidebar + light content professional UI design',
  },
];

export function AboutPage() {
  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">About</h1>
        <p className="mt-1 text-sm text-gray-500">About VulnTracker — Vulnerability Intelligence module</p>
      </div>

      {/* Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Card header */}
        <div className="bg-gray-900 px-6 py-6 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary-500/20 flex items-center justify-center shrink-0">
            <ShieldExclamationIcon className="h-7 w-7 text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white leading-tight">VulnTracker</h2>
            <p className="text-sm text-gray-400">Enterprise Vulnerability Management</p>
            <span className="mt-1 inline-block text-xs text-gray-500">Version 1.0.0</span>
          </div>
        </div>

        {/* Description */}
        <div className="px-6 py-6 space-y-4 border-b border-gray-100">
          <p className="text-sm text-gray-700 leading-relaxed">
            VulnTracker is a purpose-built, enterprise-grade platform that gives security and
            engineering teams{' '}
            <span className="font-semibold text-gray-900">real-time visibility</span> into every
            known vulnerability affecting their open-source components. By continuously ingesting
            threat intelligence from the world's leading vulnerability databases, VulnTracker
            automatically matches disclosures to your deployed software inventory and alerts the
            right people through the right channels — before issues become incidents.
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            Unlike point-in-time scanners that require manual re-runs, VulnTracker operates{' '}
            <span className="font-semibold text-gray-900">24 × 7</span> with a persistent
            component inventory. When a new CVE is published, every affected project is identified
            and its owners are notified within minutes — not days.
          </p>
        </div>

        {/* Key Features */}
        <div className="px-6 py-6 space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Key Features</h3>
          <ul className="space-y-3">
            {features.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-3">
                <Icon className="h-5 w-5 text-[#0EA5E9] shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Callout */}
        <div className="px-6 pb-6">
          <div className="rounded-xl border border-primary-100 bg-primary-50 p-4 flex gap-3 items-start">
            <LockClosedIcon className="h-5 w-5 text-primary-600 shrink-0 mt-0.5" />
            <p className="text-sm text-primary-800 leading-relaxed">
              VulnTracker transforms vulnerability management from a{' '}
              <span className="font-semibold">reactive, manual process</span> into a{' '}
              <span className="font-semibold">proactive, automated workflow</span> — reducing
              mean-time-to-remediate and eliminating the risk of missed disclosures.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-xs text-gray-400">Part of the SecurusMind platform</p>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <a href="https://securusmind.com" target="_blank" rel="noopener noreferrer"
              className="hover:text-[#0EA5E9] transition-colors">
              securusmind.com
            </a>
            <a href="mailto:contact@securusmind.com"
              className="hover:text-[#0EA5E9] transition-colors">
              Contact Support
            </a>
            <span>© 2026</span>
          </div>
        </div>
      </div>
    </div>
  );
}
